import { CalendarDays, ChevronLeft, ChevronRight, Clock3 } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ComponentProps, type KeyboardEvent, type Ref } from 'react';
import { cn } from '@/lib/utils';
import { fieldBase } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Select } from './select';

type Props = Omit<ComponentProps<'button'>, 'value' | 'onChange' | 'type' | 'ref'> & {
  value?: string | null;
  onValueChange: (value: string) => void;
  withTime?: boolean;
  min?: string;
  max?: string;
  required?: boolean;
  name?: string;
  placeholder?: string;
  ref?: Ref<HTMLButtonElement>;
};
const months = Array.from({ length: 12 }, (_, n) =>
  new Date(2026, n, 1).toLocaleDateString('pt-BR', { month: 'long' }),
);
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function parse(value?: string | null) {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const d = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isFinite(d.getTime()) && iso(d) === value.slice(0, 10) ? d : null;
}
const add = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 12);
const dayLabel = (d: Date) =>
  d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/** Datas locais, sem conversão UTC. A seleção mantém o contrato ISO dos formulários. */
export function DatePicker({
  value,
  onValueChange,
  withTime = false,
  min,
  max,
  required,
  name,
  placeholder,
  className,
  disabled,
  readOnly,
  ref,
  ...props
}: Props & { readOnly?: boolean }) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => parse(value) ?? new Date());
  const [focused, setFocused] = useState(() => iso(parse(value) ?? new Date()));
  const [pending, setPending] = useState('');
  const content = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const selected = parse(value);
  const today = iso(new Date());
  const limit = (day: string) => day < (min?.slice(0, 10) ?? '') || Boolean(max && day > max.slice(0, 10));
  const bounded = (d: Date) =>
    parse(min && iso(d) < min.slice(0, 10) ? min : max && iso(d) > max.slice(0, 10) ? max : iso(d)) ?? d;
  const pendingDate = parse(pending);
  const time = pending.slice(11, 16) || '09:00';
  const validPending =
    Boolean(pendingDate) &&
    !limit(pending.slice(0, 10)) &&
    (!min || pending >= min) &&
    (!max || pending <= max);
  const choose = (d: Date) => {
    if (limit(iso(d))) return;
    if (withTime) setPending(`${iso(d)}T${time}`);
    else {
      onValueChange(iso(d));
      setOpen(false);
    }
  };
  const move = (d: Date) => {
    const next = bounded(d);
    setCursor(next);
    setFocused(iso(next));
    content.current?.querySelector<HTMLButtonElement>(`[data-date="${iso(next)}"]`)?.focus();
  };
  const changeMonth = (n: number) => move(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1, 12));
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, date: Date) => {
    const weekday = (date.getDay() + 6) % 7;
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -weekday,
      End: 6 - weekday,
    };
    if (e.key in moves) {
      e.preventDefault();
      move(add(date, moves[e.key]!));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      const dir = e.key === 'PageUp' ? -1 : 1;
      const next = new Date(
        date.getFullYear() + (e.shiftKey ? dir : 0),
        date.getMonth() + (e.shiftKey ? 0 : dir),
        1,
        12,
      );
      next.setDate(Math.min(date.getDate(), new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()));
      move(next);
    }
  };
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() =>
      content.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus(),
    );
    return () => cancelAnimationFrame(id);
  }, [open, focused]);
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 12);
  const start = add(first, -((first.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, n) => add(start, n));
  const minYear = parse(min)?.getFullYear() ?? Math.min(new Date().getFullYear() - 100, cursor.getFullYear());
  const maxYear = parse(max)?.getFullYear() ?? Math.max(new Date().getFullYear() + 100, cursor.getFullYear());
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          const date = bounded(selected ?? new Date());
          setCursor(date);
          setFocused(iso(date));
          setPending(
            selected
              ? `${iso(selected)}${withTime ? `T${value?.slice(11, 16) || '09:00'}` : ''}`
              : `${iso(date)}${withTime ? 'T09:00' : ''}`,
          );
        }
        setOpen(next);
      }}
    >
      {name && <input type="hidden" name={name} value={value ?? ''} />}
      <PopoverTrigger asChild>
        <button
          {...props}
          ref={ref}
          type="button"
          disabled={disabled || readOnly}
          aria-required={required || undefined}
          className={cn(
            fieldBase,
            'flex h-10 min-w-0 items-center justify-between gap-2 text-left tabular-nums',
            !selected && 'text-fg-subtle',
            className,
          )}
        >
          <span className="truncate">
            {selected
              ? `${selected.toLocaleDateString('pt-BR')}${withTime ? ` às ${value?.slice(11, 16) || '09:00'}` : ''}`
              : (placeholder ?? (withTime ? 'Data e horário' : 'Selecionar data'))}
          </span>
          <CalendarDays className="size-4 shrink-0 text-fg-subtle" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={content}
        align="start"
        aria-label={withTime ? 'Escolher data e horário' : 'Escolher data'}
        className="w-[320px] max-w-[calc(100vw-24px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto border border-line-strong p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-3 flex items-center gap-1">
          <Select
            aria-label="Mês"
            className="h-8 flex-1 bg-transparent px-2 text-[13px] shadow-none capitalize"
            value={String(cursor.getMonth())}
            onValueChange={(m) => move(new Date(cursor.getFullYear(), Number(m), 1, 12))}
            options={months.map((m, n) => ({ value: String(n), label: m }))}
          />
          <Select
            aria-label="Ano"
            className="h-8 w-[82px] bg-transparent px-2 text-[13px] shadow-none"
            value={String(cursor.getFullYear())}
            onValueChange={(y) => move(new Date(Number(y), cursor.getMonth(), 1, 12))}
            options={Array.from({ length: Math.max(0, maxYear - minYear + 1) }, (_, i) => ({
              value: String(minYear + i),
              label: String(minYear + i),
            }))}
          />
          {[-1, 1].map((dir) => (
            <button
              key={dir}
              type="button"
              aria-label={dir === -1 ? 'Mês anterior' : 'Próximo mês'}
              disabled={
                dir === -1
                  ? Boolean(min && iso(add(first, -1)) < min.slice(0, 10))
                  : Boolean(
                      max && iso(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)) > max.slice(0, 10),
                    )
              }
              onClick={() => changeMonth(dir)}
              className="flex size-8 shrink-0 items-center justify-center rounded-[9px] text-fg-muted transition-colors hover:bg-hover focus-visible:outline-2 focus-visible:outline-signal disabled:opacity-30"
            >
              {dir === -1 ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}
            </button>
          ))}
        </div>
        <p id={labelId} className="sr-only" aria-live="polite">
          {months[cursor.getMonth()]} de {cursor.getFullYear()}
        </p>
        <div role="grid" aria-labelledby={labelId}>
          <div role="row" className="mb-1 grid grid-cols-7">
            {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((d) => (
              <span role="columnheader" key={d} className="py-2 text-center text-[10px] text-fg-subtle">
                {d}
              </span>
            ))}
          </div>
          {Array.from({ length: 6 }, (_, row) => (
            <div role="row" key={row} className="grid grid-cols-7 gap-y-1">
              {days.slice(row * 7, row * 7 + 7).map((date) => {
                const key = iso(date),
                  picked = key === (withTime ? pending.slice(0, 10) : value?.slice(0, 10));
                return (
                  <div
                    role="gridcell"
                    aria-selected={picked}
                    key={key}
                    className="flex items-center justify-center"
                  >
                    <button
                      type="button"
                      data-date={key}
                      aria-label={dayLabel(date)}
                      aria-current={key === today ? 'date' : undefined}
                      disabled={limit(key)}
                      tabIndex={focused === key ? 0 : -1}
                      onKeyDown={(e) => onKey(e, date)}
                      onClick={() => choose(date)}
                      className={cn(
                        'relative flex size-9 items-center justify-center rounded-[10px] text-[12px] tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal disabled:pointer-events-none disabled:opacity-25',
                        picked ? 'bg-signal font-semibold text-signal-fg' : 'hover:bg-hover',
                        !picked && date.getMonth() !== cursor.getMonth() && 'text-fg-subtle',
                        !picked && key === today && 'font-semibold text-signal',
                      )}
                    >
                      {date.getDate()}
                      {key === today && !picked && (
                        <span className="absolute bottom-1 size-[3px] rounded-full bg-signal" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {withTime && (
          <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
            <Clock3 className="size-4 text-fg-subtle" />
            <span className="mr-auto text-xs text-fg-muted">Horário</span>
            <Select
              aria-label="Hora"
              className="h-8 w-[72px] px-2 text-xs"
              value={time.slice(0, 2)}
              onValueChange={(h) => setPending(`${pending.slice(0, 10)}T${h}:${time.slice(3, 5)}`)}
              options={Array.from({ length: 24 }, (_, n) => ({
                value: String(n).padStart(2, '0'),
                label: String(n).padStart(2, '0'),
              }))}
            />
            <span className="text-fg-subtle">:</span>
            <Select
              aria-label="Minuto"
              className="h-8 w-[72px] px-2 text-xs"
              value={time.slice(3, 5)}
              onValueChange={(m) => setPending(`${pending.slice(0, 10)}T${time.slice(0, 2)}:${m}`)}
              options={Array.from({ length: 60 }, (_, n) => ({
                value: String(n).padStart(2, '0'),
                label: String(n).padStart(2, '0'),
              }))}
            />
          </div>
        )}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-line pt-3">
          <button
            type="button"
            disabled={required || !value}
            onClick={() => {
              onValueChange('');
              setOpen(false);
            }}
            className="rounded-[8px] px-2 py-1.5 text-xs text-fg-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-signal disabled:opacity-30"
          >
            Limpar
          </button>
          <button
            type="button"
            disabled={limit(today)}
            onClick={() => {
              move(new Date());
              choose(new Date());
            }}
            className="rounded-[8px] px-2 py-1.5 text-xs text-fg-muted hover:bg-hover focus-visible:outline-2 focus-visible:outline-signal disabled:opacity-30"
          >
            Hoje
          </button>
          {withTime && (
            <button
              type="button"
              disabled={!validPending}
              onClick={() => {
                onValueChange(pending);
                setOpen(false);
              }}
              className="rounded-[9px] bg-signal px-3 py-2 text-xs font-medium text-signal-fg hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal disabled:opacity-40"
            >
              Aplicar
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
